import { inject, Injectable } from '@angular/core';
import { combineLatest, filter, take } from 'rxjs';
import { DoFiltersService } from '@app/main/main-content/display-options/services/do-filters/do-filters.service';
import { DoFilterKey } from '@app/main/main-content/display-options/enums/do-filter-key.enum';
import {
  CorrelationOptionKey,
  LoggerTypeOptionKey,
} from '@app/main/main-content/display-options/enums/do-filter-option-key.enum';
import { LdKpiMetric } from '@app/main/leak-detection/enums/ld-kpi-metric.enum';
import { IDoFilter } from '@app/main/main-content/display-options/interfaces/do-filter.interface';
import { LdCoordinatorStore } from '@app/main/leak-detection/services/leak-detection-coordinator/leak-detection-coordinator.store';
import {
  LdActionPlace,
  LdActionType,
} from '@app/main/leak-detection/services/leak-detection-coordinator/leak-detection-coordinator.actions';
import { LdTabs } from '@app/main/leak-detection/enums/ld-tabs';
import { DialogTrackerService } from '@app/core/services/dialog-tracker/dialog-tracker.service';
import { DialogApplyFilterConfirmationComponent } from '@app/main/leak-detection/dialogs/dialog-apply-filter-confirmation/dialog-apply-filter-confirmation.component';
import {
  DEFAULT_CORRELATIONS_SORT_FIELD,
  DEFAULT_CORRELATIONS_SORT_ORDER,
  DEFAULT_LOGGER_NOISE_SORT_FIELD,
  DEFAULT_LOGGER_NOISE_SORT_ORDER,
  SKIP_CONFIRMATION_KEY,
} from '@app/main/leak-detection/constants/leak-detection.constants';
import { selectLeakDetectionShowSummaryOnLogin } from '@app/main/leak-detection/store/leak-detection-selection/leak-detection-selection.selectors';
import { setLeakDetectionShowSummaryOnLogin } from '@app/main/leak-detection/store/leak-detection-selection/leak-detection-selection.actions';
import { IDialogConfirmationResult } from '@app/main/main-content/display-options/interfaces/dialog-confirmation-result.interface';
import { DoToolbarService } from '@app/main/main-content/display-options/services/do-toolbar/do-toolbar.service';
import { Store } from '@ngrx/store';
import { selectMainProjectId } from '@app/main/main-screen/store/main-selection/main-shared-selection/main-shared-selection.selectors';
import { selectProjectEntities } from '@app/main/main-screen/store/projects/projects.selectors';
import { LeakDetectionMode } from '@app/core/enums/leak-detection-mode.enum';
import { MessageService } from 'primeng/api';
import { MessageConfigsService } from '@app/core/services/message-configs/message-configs.service';
import { ToastSeverity } from '@app/shared/components/toast-content/toast-content.component';
import { ToastKey } from '@app/core/enums/toast-key.enum';
import { ToastMessageId } from '@app/core/enums/toast-message-id.enum';
import { _, TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class LeakDetectionKpiSelectionService {
  private doFiltersService: DoFiltersService = inject(DoFiltersService);
  private doToolbarService: DoToolbarService = inject(DoToolbarService);
  private ldCoordinatorStore: LdCoordinatorStore = inject(LdCoordinatorStore);
  private dialogTrackerService: DialogTrackerService = inject(DialogTrackerService);
  private store: Store = inject(Store);
  private messageService: MessageService = inject(MessageService);
  private messageConfigsService: MessageConfigsService = inject(MessageConfigsService);
  private translateService: TranslateService = inject(TranslateService);
  private correlationsSortModified = false;
  private loggerNoiseSortModified = false;

  public showSummaryOnLogin$ = this.store.select(selectLeakDetectionShowSummaryOnLogin);

  public clearSkipConfirmationFlag() {
    localStorage.removeItem(SKIP_CONFIRMATION_KEY);
  }

  public setShowSummaryOnLogin(value: boolean) {
    this.store.dispatch(setLeakDetectionShowSummaryOnLogin({ showSummaryOnLogin: value }));
  }

  public updateCorrelationsSortState(field: string | null | undefined, order: number | null | undefined) {
    const isDefault = field === DEFAULT_CORRELATIONS_SORT_FIELD && order === DEFAULT_CORRELATIONS_SORT_ORDER;
    this.correlationsSortModified = !isDefault;
  }

  public updateLoggerNoiseSortState(field: string | null | undefined, order: number | null | undefined) {
    const isDefault = field === DEFAULT_LOGGER_NOISE_SORT_FIELD && order === DEFAULT_LOGGER_NOISE_SORT_ORDER;
    this.loggerNoiseSortModified = !isDefault;
  }

  public selectMetric(metric: LdKpiMetric) {
    // TODO: This flag should be stored on server instead of localstorage. Rework this after API implementation.
    if (localStorage.getItem(SKIP_CONFIRMATION_KEY) === 'true') {
      this.applyMetric(metric);
      return;
    }

    this.doFiltersService.filters$
      .pipe(
        filter(filters => filters.length > 0),
        take(1)
      )
      .subscribe(filters => {
        if (!this.needsConfirmation(metric, filters)) {
          this.applyMetric(metric);
          return;
        }

        const ref = this.dialogTrackerService.regAndOpen(DialogApplyFilterConfirmationComponent, {
          ...this.dialogTrackerService.getMainContentDialogConfig(),
          width: '25rem',
        });

        ref.onClose.pipe(take(1)).subscribe((result: IDialogConfirmationResult | undefined) => {
          if (!result?.confirmed) return;

          if (result.doNotShowAgain) {
            localStorage.setItem(SKIP_CONFIRMATION_KEY, 'true');
          }

          this.applyMetric(metric);
        });
      });
  }

  private needsConfirmation(metric: LdKpiMetric, filters: IDoFilter[]): boolean {
    if (metric === LdKpiMetric.CorrelationsNew || metric === LdKpiMetric.CorrelationsTotal) {
      if (this.correlationsSortModified) return true;
    }
    if (metric === LdKpiMetric.PredictionPossible || metric === LdKpiMetric.PredictionProbable) {
      if (this.loggerNoiseSortModified) return true;
    }
    return this.wouldDeactivateOptions(metric, filters);
  }

  private wouldDeactivateOptions(metric: LdKpiMetric, filters: IDoFilter[]): boolean {
    switch (metric) {
      case LdKpiMetric.CorrelationsNew:
      case LdKpiMetric.CorrelationsTotal: {
        const correlationFilter = filters.find(filter => filter.key === DoFilterKey.Correlation);
        if (correlationFilter?.enabled) {
          if (correlationFilter.selected.some(o => o.key === CorrelationOptionKey.Suppressed)) return true;
        }
        return false;
      }
      case LdKpiMetric.PredictionPossible:
      case LdKpiMetric.PredictionProbable: {
        const loggerTypeFilter = filters.find(
          f => f.key === DoFilterKey.LoggerType || f.key === DoFilterKey.LoggersByAIPredictor
        );
        if (loggerTypeFilter?.enabled) {
          const targetKey =
            metric === LdKpiMetric.PredictionPossible ? LoggerTypeOptionKey.Possible : LoggerTypeOptionKey.Probable;
          if (loggerTypeFilter.selected.some(o => o.key !== targetKey)) return true;
        }
        return false;
      }
      default:
        return false;
    }
  }

  private applyMetric(metric: LdKpiMetric): void {
    this.doToolbarService.toggleSyncTableWithMapFilter(true);
    this.correlationsSortModified = false;
    this.loggerNoiseSortModified = false;
    this.doFiltersService.filters$
      .pipe(
        filter(filters => filters.length > 0),
        take(1)
      )
      .subscribe(filters => {
        this.applyMetricFilters(filters, metric);
        this.switchTab(metric);
        this.showFilterAppliedMessage(metric);
      });
  }

  private applyMetricFilters(currentFilters: IDoFilter[], metric: LdKpiMetric): void {
    switch (metric) {
      case LdKpiMetric.CorrelationsNew:
      case LdKpiMetric.CorrelationsTotal: {
        const corrFilter = currentFilters.find(f => f.key === DoFilterKey.Correlation);
        if (corrFilter) {
          const updated = {
            ...corrFilter,
            enabled: true,
            selected: corrFilter.options.filter(o => o.key !== CorrelationOptionKey.Suppressed),
          };
          this.doFiltersService.setFilters(
            currentFilters.map(f => (f.key === DoFilterKey.Correlation ? updated : f)),
            updated
          );
        }
        break;
      }

      case LdKpiMetric.PredictionPossible:
      case LdKpiMetric.PredictionProbable: {
        const loggerTypeFilter = currentFilters.find(
          f => f.key === DoFilterKey.LoggerType || f.key === DoFilterKey.LoggersByAIPredictor
        );
        if (loggerTypeFilter) {
          const targetKey =
            metric === LdKpiMetric.PredictionPossible ? LoggerTypeOptionKey.Possible : LoggerTypeOptionKey.Probable;
          const updated = {
            ...loggerTypeFilter,
            enabled: true,
            selected: loggerTypeFilter.options.filter(o => o.key === targetKey),
          };
          this.doFiltersService.setFilters(
            currentFilters.map(f => (f.key === loggerTypeFilter.key ? updated : f)),
            updated
          );
        }
        break;
      }

      default:
        if (currentFilters.length > 0) {
          this.doFiltersService.setFilters(currentFilters, currentFilters[0]);
        }
    }
  }

  private switchTab(metric: LdKpiMetric): void {
    let tab: LdTabs | null = null;

    if (metric === LdKpiMetric.CorrelationsNew || metric === LdKpiMetric.CorrelationsTotal) {
      tab = LdTabs.Correlations;
    } else if (metric === LdKpiMetric.PredictionPossible || metric === LdKpiMetric.PredictionProbable) {
      tab = LdTabs.LoggerNoise;
    }

    if (tab) {
      this.ldCoordinatorStore.dispatch({ type: LdActionType.TabChange, tab, place: LdActionPlace.TabsPanel });
    }
  }

  private showFilterAppliedMessage(metric: LdKpiMetric): void {
    if (metric === LdKpiMetric.CorrelationsNew || metric === LdKpiMetric.CorrelationsTotal) {
      const filterKey = metric === LdKpiMetric.CorrelationsNew ? _('@@shared-new__text') : _('@@shared-total__text');
      this.addViewingMessage(_('@@shared-correlations__text'), filterKey);
      return;
    }

    combineLatest([this.store.select(selectMainProjectId), this.store.select(selectProjectEntities)])
      .pipe(take(1))
      .subscribe(([projectId, projects]) => {
        const mode = projects[projectId!]?.leakDetectionMode;
        const entityKey =
          mode === LeakDetectionMode.LeakScore ? _('@@ld-leak-score__text') : _('@@shared-ai-predictor__text');
        const filterKey =
          metric === LdKpiMetric.PredictionPossible
            ? _('@@shared-possible-leak__label')
            : _('@@shared-probable-leak__label');
        this.addViewingMessage(entityKey, filterKey);
      });
  }

  private addViewingMessage(entityKey: string, filterKey: string): void {
    this.messageService.add({
      ...this.messageConfigsService.getMainContentMessageConfig(),
      key: ToastKey.MainContent,
      id: ToastMessageId.KpiFilterApplied,
      severity: ToastSeverity.Success,
      summary: this.translateService.instant(_('@@ld-kpi-currently-viewing__text'), {
        entity: this.translateService.instant(entityKey),
        filter: this.translateService.instant(filterKey),
      }),
    });
  }
}
